import { ref, computed } from 'vue';
import { computedAsync, createSharedComposable, tryOnBeforeMount } from '@vueuse/core';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import type { LsPlgUsage } from '@eureka/services/src/apis/common.api.types';
import { EurekaPackageType } from '@eureka/services/src/apis/common.api.types';
import { controlDeferred, type IControl } from '@eureka/utils/control';
import { openClueReport } from '@eureka/shared/clue-report';
import dayjs from 'dayjs';
import { StripeApi } from '@eureka/services/src/apis/stripe.api';
import type { ICreatePaymentSessionRequest, ISubscriptionListDTO } from '@eureka/services/src/apis/stripe.api.types';
import { combineHash } from '@eureka/utils/url';
import type { LsEditionType, UsageItem } from '../types';
import { PRICE_MAP } from '../constants';
import type { Account } from '@patsnap-utils/biz';
import { accountDeferred } from '@eureka/utils/account';
import { lsPlanDeferred } from '@eureka/utils/ls-plan';

/**
 * LS 版本用量管理 - 整合业务逻辑和 API 调用
 */
export const useLsEditionUsage = createSharedComposable(() => {
    const lsPlgUsage = ref<LsPlgUsage | null>(null);
    const isLoading = ref(false);
    const error = ref<Error | null>(null);
    const currentSubscription = ref<ISubscriptionListDTO | null>(null);

    const controlInfo = ref<IControl | null>(null);
    const account = ref<Account | null>(null);

    tryOnBeforeMount(async() => {
        controlInfo.value = await controlDeferred.promise;
        account.value = await accountDeferred.promise;
    })

    /**
     * 计算进度百分比
     */
    function getProgressPercentage(current: number = 0, max: number = 0): number {
        if (!max || max === 0) return 0;
        return Math.min(Math.round((current / max) * 100), 100);
    }

    // ========== 用户版本判断 ==========
    const userEdition = computed<LsEditionType>(() => {
        // return 'personal_pro' // 临时修改：始终返回 personal_basic
        return controlInfo.value?.ls_plg_role || 'personal_basic';
    });

    const editionBadgeText = computed(() => {
        const map: Record<LsEditionType, string> = {
            personal_basic: 'Basic',
            personal_pro: 'Pro',
        };
        return map[userEdition.value];
    });

    // ========== Pro 用户判断（付费用户：enterprise/personal_pro）==========
    const isLsPro = computed(() => {
        const proTypes: LsEditionType[] = ['personal_pro'];
        return proTypes.includes(userEdition.value);
    });

    // ========== Usage 数据获取 ==========

    /**
     * 奖励积分 用量
     */
    const earnCredits = computed<UsageItem | null>(() => {
        const usage = lsPlgUsage.value?.usage.find(i => i.key === 'reward_credit')
        if (!usage || !usage.support) return null

        return {
            current: usage.current_amount,
            available: usage.available_amount,
            max: usage.max_amount,
            progress: getProgressPercentage(usage.current_amount, usage.max_amount),
        };
    });

    /**
     * 每日积分 用量
     */
    const creditsPerMonth = computed<UsageItem | null>(() => {
        const usage = lsPlgUsage.value?.usage.find(i => i.key === 'privilege_credit')
        if (!usage || !usage.support) return null

        return {
            current: usage.current_amount,
            available: usage.available_amount,
            max: usage.max_amount,
            progress: getProgressPercentage(usage.current_amount, usage.max_amount),
        };
    });

    /**
     * 购买的积分
     */
    const addonCredits = computed<UsageItem | null>(() => {
        const usage = lsPlgUsage.value?.usage.find(i => i.key === 'addon_credit')
        if (!usage || !usage.support) return null

        return {
            current: usage.current_amount,
            available: usage.available_amount,
            max: usage.max_amount,
            progress: getProgressPercentage(usage.current_amount, usage.max_amount),
        };
    });

    /**
     * 总积分计算
     */
    const totalUsedCredits = computed(() => {
        return (earnCredits.value?.current || 0) + (creditsPerMonth.value?.current || 0) + (addonCredits.value?.current || 0);
    })

    /**
     * 总可用积分
     */
    const totalAvailableCredits = computed(() => {
        return (earnCredits.value?.available || 0) + (creditsPerMonth.value?.available || 0) + (addonCredits.value?.available || 0);
    })

    /**
     * Projects 用量（MB）
     */
    const projects = computed<UsageItem | null>(() => {
        const usage = lsPlgUsage.value?.usage.find(i => i.key === 'project_storage')
        if (!usage || !usage.support) return null

        return {
            current: usage.current_amount,
            available: usage.available_amount,
            max: usage.max_amount,
            progress: getProgressPercentage(usage.current_amount, usage.max_amount),
        };
    });

    /**
     * 奖励空间 用量
     */
    const rewardStorage = computed<UsageItem | null>(() => {
        const usage = lsPlgUsage.value?.usage.find(i => i.key === 'reward_storage')
        if (!usage || !usage.support) return null

        return {
            current: usage.current_amount,
            available: usage.available_amount,
            max: usage.max_amount,
            progress: getProgressPercentage(usage.current_amount, usage.max_amount),
        };
    });

    /**
     * 总已使用项目空间
     */
    const totalUsedProjectStorage = computed(() => {
        return (projects.value?.current || 0) + (rewardStorage.value?.current || 0);
    })

    /**
     * 总项目空间（MB）
     */
    const totalProjectStorage = computed(() => {
        return (projects.value?.max || 0) + (rewardStorage.value?.max || 0);
    })

    // 计算项目空间使用进度百分比
    const projectProgressPercentage = computed(() => {
        const percentage = (totalUsedProjectStorage.value / totalProjectStorage.value) * 100;
        return Math.min(Math.max(percentage, 0), 100);
    });

    /**
     * 过期日期倒计时天数
     * 规则：
     * 1. personal_basic 用户不展示：返回 null
     * 2. 其他用户：如果过期日期 <= 14天，返回剩余天数（number）
     * 3. 过期日期包含当天（即过期日当天还能使用），所以最小显示为 1 day left
     * 4. 其他情况返回 null
     */
    const expireDaysLeft = computed<number | null>(() => {
        // personal_basic 用户不展示
        if (userEdition.value === 'personal_basic') {
            return null;
        }

        // 没有用量数据或没有过期日期
        if (!lsPlgUsage.value?.role_expire_at) {
            return null;
        }

        // role_expire_at 格式为 YYYY-MM-DD，统一到当天开始时间进行比较
        const expireAt = dayjs(lsPlgUsage.value.role_expire_at).startOf('day');
        const today = dayjs().startOf('day');
        const daysLeft = expireAt.diff(today, 'day') + 1;

        // 只有 <= 14 天时才展示
        if (daysLeft <= 0) {
            return null; // 已过期
        }

        if (daysLeft <= 14) {
            return daysLeft;
        }

        return null;
    });

    // ========== 展示逻辑 ==========

    /**
     * Earn Credits: 有用量则展示
     */
    // const showEarnCredits = computed(() => {
    //     if (!earnCredits.value) return false;
    //     const { max, current } = earnCredits.value;
    //     return max > 0 || current > 0;
    // });

    /**
     * Credits/month: 有最大额度则展示
     */
    // const showCreditsPerMonth = computed(() => {
    //     if (!creditsPerMonth.value) return false;
    //     return creditsPerMonth.value.max > 0;
    // });

    /**
     * Projects: 有最大额度则展示
     */
    // const showProjects = computed(() => {
    //     if (!projects.value) return false;
    //     return projects.value.max > 0;
    // });

    /**
     * 是否有任何用量项
     */
    // const hasAnyUsageItems = computed(() => {
    //     return showEarnCredits.value || showCreditsPerMonth.value || showProjects.value;
    // });

    /**
     * Upgrade 按钮: 仅免费用户和试用用户显示
     */
    const showUpgradeButton = computed(() => {
        const upgradeTypes: LsEditionType[] = ['personal_basic'];
        return upgradeTypes.includes(userEdition.value);
    });

    // ========== 用量上限检查 ==========

    /**
     * 积分用量是否达到上限
     */
    const isCreditLimitReached = computed(() => {
        return !earnCredits.value?.available && !creditsPerMonth.value?.available && !addonCredits.value?.available;
    });

    /**
     * Project 用量是否达到上限
     */
    const isProjectStorageLimitReached = computed(() => {
        return !projects.value?.available && !rewardStorage.value?.available;
    });

    /**
     * 转换成前端的i18n类型
     */
    const planType = computed(() => {
        const map = {
            personal_basic: 'basic',
            personal_pro: 'pro',
        } as const;
        return map[userEdition.value as keyof typeof map];
    })

    /**
     * 是否取消了订阅
     */
    const isCanceled = computed(() => {
        return !!currentSubscription.value?.cancel_at;
    })

    /**
     * 当前订阅中匹配 PRICE_MAP 的第一个 item
     */
    const currentItemSubscription = computedAsync(async() => {
        const currentSubscriptionValue = currentSubscription.value
        if (!currentSubscriptionValue) return null;
        const currentEdition = userEdition.value;

        const planValue = await lsPlanDeferred.promise;
        const priceMap = planValue === 'economy' ? PRICE_MAP.B : PRICE_MAP.A;

        const validPriceIds = priceMap[currentEdition]?.map(price => price.priceId) || [];

        return currentSubscriptionValue.items.find(item =>
            validPriceIds.includes(item.price_id)
        ) || null;
    })

    const isPersonal = computed(() => {
        return !account.value?._info?.company_id;
    })

    // ========== API 调用 ==========

    /**
     * 获取 LS PLG 用量数据
     */
    async function fetchLsPlgUsage() {
        isLoading.value = true;
        error.value = null;

        try {
            const res = await CommonApis.getLsPlgUsage();
            lsPlgUsage.value = res;
        } catch (e) {
            error.value = e as Error;
            console.error('[useLsEditionUsage] Failed to fetch LS PLG usage:', e);
        } finally {
            isLoading.value = false;
        }
    }

    async function createPaymentSession(params: ICreatePaymentSessionRequest) {
        const res = await StripeApi.createPaymentSession(params);
        return res;
    }

    /**
     * 获取用户订阅列表
     */
    async function fetchSubscriptionList() {
        const res = await StripeApi.getSubscriptionList()
        const planValue = await lsPlanDeferred.promise;
        const priceMap = planValue === 'economy' ? PRICE_MAP.B : PRICE_MAP.A;

        // 获取当前版本的所有价格ID（包含年付和月付）
        const validPriceIds = priceMap[userEdition.value]?.map(price => price.priceId) || [];

        // 筛选激活状态且包含年付或月付价格ID的订阅
        const activeSubscriptions = res.filter(subscription => {
            // 必须是激活状态或试用状态
            if (subscription.subscription_status !== 'active') return false;

            // items 中必须包含年付或月付的 price_id
            const hasValidPrice = subscription.items.some(item =>
                validPriceIds.includes(item.price_id)
            );

            return hasValidPrice;
        });

        if (activeSubscriptions.length > 0) {
            currentSubscription.value = activeSubscriptions[0];
        }
    }

    /**
     *
     * @returns 查看账单
     */
    async function handleViewInvoices() {
        if (!currentSubscription.value) return;
        const res = await StripeApi.getInvoices({
            subscription_id: currentSubscription.value.subscription_id,
            return_url: window.location.href,
        });
        if (!res) return;
        window.open(res, '_blank', 'noopener,noreferrer');
    }

    /**
     * 取消订阅
     * @returns
     */
    async function handleCancelPlan(reason: string, otherReason?: string) {
        if (!currentSubscription.value) return;
        await StripeApi.cancelSubscription({
            subscription_id: currentSubscription.value.subscription_id,
            reason,
            ...(otherReason ? { remark: otherReason } : {}),
        });
    }

    /**
     * 恢复订阅
     * @returns
     */
    async function handleResumePlan() {
        if (!currentSubscription.value) return;
        await StripeApi.resumeSubscription({
            subscription_id: currentSubscription.value.subscription_id,
        });
    }

    /**
     * 升级逻辑 - 跳转到 LS 套餐页面
     */
    const handleUpgrade = () => {
        location.hash = combineHash('', 'user-setting/package/ls/plg');
    };

    /**
     * 检查用量限制并在达到上限时显示联系销售弹窗
     * @param type - 检查类型：'credit' 积分用量 或 'project_storage' 项目存储用量
     * @returns Promise<boolean> - true: 未达到上限可以继续操作，false: 已达到上限不能继续操作
     */
    const checkUsageLimitAndShowDialog = async(type: 'credit' | 'project_storage'): Promise<boolean> => {
        try {
            // 先获取最新的用量数据
            await fetchLsPlgUsage();

            // 检查是否达到上限
            let isLimitReached = false;

            if (type === 'credit') {
                isLimitReached = isCreditLimitReached.value;
            } else if (type === 'project_storage') {
                isLimitReached = isProjectStorageLimitReached.value;
            }

            // 如果达到上限，打开联系销售弹窗
            if (isLimitReached) {
                // 使用 openClueReport 打开联系销售弹窗
                await openClueReport({
                    pkgType: EurekaPackageType.LS_COMMERCIAL,
                    version: 'LS_PLG',
                });

                return false; // 达到上限，不能继续操作
            }

            return true; // 未达到上限，可以继续操作
        } catch (e) {
            console.error('[useLsEditionUsage] Failed to check usage limit:', e);
            // 如果检查失败，为了不阻止用户操作，返回 true
            return true;
        }
    };

    /**
     * 检查用户是否已有包含 PRICE_MAP 中任意 priceId 的有效订阅
     * @returns Promise<boolean> - true: 已有有效订阅, false: 没有有效订阅
     */
    async function checkHasActiveSubscription(): Promise<boolean> {
        const res = await StripeApi.getSubscriptionList();

        // 从 PRICE_MAP 中提取所有的 priceId(所有版本)
        const allPriceIds: string[] = [];

        Object.values(PRICE_MAP).forEach(p => {
            Object.values(p).forEach(priceList => {
                priceList.forEach(priceItem => {
                    allPriceIds.push(priceItem.priceId);
                });
            });
        });

        // 筛选激活状态且包含 PRICE_MAP 中任意 priceId 的订阅
        const activeSubscriptions = res.filter(subscription => {
            // 必须是激活状态
            if (subscription.subscription_status !== 'active') return false;

            // items 中必须包含 PRICE_MAP 中的任意 price_id
            const hasValidPrice = subscription.items.some(item =>
                allPriceIds.includes(item.price_id)
            );

            return hasValidPrice;
        });

        return activeSubscriptions.length > 0;
    }

    return {
        // 数据状态
        isLoading,
        error,
        userEdition,
        currentSubscription,
        currentItemSubscription,
        editionBadgeText,
        isLsPro,
        expireDaysLeft,
        planType,
        isCanceled,

        // Usage 数据
        earnCredits,
        creditsPerMonth,
        addonCredits,
        projects,
        rewardStorage,
        lsPlgUsage,
        totalUsedCredits,
        totalAvailableCredits,
        totalUsedProjectStorage,
        totalProjectStorage,
        projectProgressPercentage,
        isPersonal,

        // 展示逻辑
        // showEarnCredits,
        // showCreditsPerMonth,
        // showProjects,
        showUpgradeButton,

        // 用量上限检查
        isCreditLimitReached,
        isProjectStorageLimitReached,

        // 工具函数
        handleUpgrade,
        fetchLsPlgUsage,
        checkUsageLimitAndShowDialog,
        createPaymentSession,
        fetchSubscriptionList,
        handleViewInvoices,
        handleCancelPlan,
        handleResumePlan,
        checkHasActiveSubscription,
    };
});
